import React, { useState, useEffect } from 'react';
import { 
  Globe, Users, Edit3, Trash2, CheckCircle, Search, DollarSign, X, Eye, EyeOff, 
  ShieldAlert, BadgeCheck, Phone, Check, Shield, TrendingUp, Sparkles, Building2, 
  Store, Lock, ArrowUpRight, Copy, Link, MapPin, Ban, Star, Smartphone, Activity,
  Award, Unlock, RefreshCw, XCircle, Coins, PlusCircle, AlertTriangle, CheckCircle2, Crown, Settings2,
  AlertCircle, ExternalLink, ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';
import { FilterCountrySelect, SearchableCountrySelect, CountryBadge } from '../components/SearchableCountrySelect';
import { sellerRateAdminAPI } from '../utils/api';

const SellersManagement = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewFilter, setViewFilter] = useState('ALL'); // 'ALL' | 'TOP_APP'
  const [countryFilter, setCountryFilter] = useState('ALL');

  // Selling Rate Ceiling (Admin Policy Rule)
  const [rateConfig, setRateConfig] = useState({ max_coins_per_rupee: 20.00, default_coins_per_rupee: 20.00 });
  const [isEditingRateCap, setIsEditingRateCap] = useState(false);
  const [rateCapInput, setRateCapInput] = useState('');
  const [savingRateCap, setSavingRateCap] = useState(false);

  // Seller Coin Slabs (INR -> Coins)
  const [sellerCoinSlabs, setSellerCoinSlabs] = useState([]);
  const [savingSellerCoinSlabs, setSavingSellerCoinSlabs] = useState(false);
  const [sellerCoinSlabSaveSuccess, setSellerCoinSlabSaveSuccess] = useState(false);
  const [sellerUsdRate, setSellerUsdRate] = useState('94.00');

  // Modal states Seller Rate Customization Modal
  const [editingSellerRate, setEditingSellerRate] = useState(null); // { id, name, currentRate }
  const [sellerRateInput, setSellerRateInput] = useState('');
  const [savingSellerRate, setSavingSellerRate] = useState(false);

  // Deduct Seller Balance Modal State
  const [deductingSeller, setDeductingSeller] = useState(null);
  const [deductAmount, setDeductAmount] = useState('');
  const [deductReason, setDeductReason] = useState('');
  const [deductLoading, setDeductLoading] = useState(false);
  const [deductError, setDeductError] = useState('');
  const [deductSuccess, setDeductSuccess] = useState('');

  // Add Coins to Seller Wallet Modal State
  const [addingCoinsSeller, setAddingCoinsSeller] = useState(null);
  const [addCoinAmount, setAddCoinAmount] = useState('');
  const [addCoinReason, setAddCoinReason] = useState('');
  const [addCoinLoading, setAddCoinLoading] = useState(false);
  const [addCoinError, setAddCoinError] = useState('');
  const [addCoinSuccess, setAddCoinSuccess] = useState('');

  // Seller Payment Disputes (Frozen User Coins)
  const [adminDisputes, setAdminDisputes] = useState([]);
  const [loadingAdminDisputes, setLoadingAdminDisputes] = useState(false);
  const [showDisputesModal, setShowDisputesModal] = useState(false);
  const [resolvingDisputeId, setResolvingDisputeId] = useState(null);
  const [disputeAdminNote, setDisputeAdminNote] = useState({});

  // Country flags helper
  const countryFlags = {
    India: '🇮🇳',
    USA: '🇺🇸',
    'United States': '🇺🇸',
    UAE: '🇦🇪',
    'United Arab Emirates': '🇦🇪',
    Pakistan: '🇵🇰',
    Bangladesh: '🇧🇩',
    'Saudi Arabia': '🇸🇦',
    UK: '🇬🇧',
    'United Kingdom': '🇬🇧',
    Canada: '🇨🇦',
    Australia: '🇦🇺',
    Nepal: '🇳🇵',
    Kuwait: '🇰🇼',
    Qatar: '🇶🇦',
    Oman: '🇴🇲'
  };
  const getFlag = (country) => countryFlags[country] || '🌐';

  // Confirmation Modal State
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ id: null, status: null, title: '' });

  // Add Seller Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sellerForm, setSellerForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    whatsapp_number: '',
    aadhar_card: '',
    country: '',
    commission_percentage: '10.00',
    coins_per_rupee: '20.00',
    is_company: false
  });

  const countriesList = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
  ];

  useEffect(() => {
    fetchSellers();
    fetchRateConfig();
    fetchAdminDisputes();
  }, []);

  const fetchAdminDisputes = async () => {
    setLoadingAdminDisputes(true);
    try {
      const res = await sellerRateAdminAPI.getSellerDisputes();
      if (res.data?.success) {
        setAdminDisputes(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching seller disputes:", err);
    } finally {
      setLoadingAdminDisputes(false);
    }
  };

  const handleResolveDispute = async (disputeId, action) => {
    const note = disputeAdminNote[disputeId] || '';
    const confirmMsg = action === 'REFUND_TO_SELLER' 
      ? 'Confirm: Return frozen coins to the SELLER (fake payment confirmed)?'
      : 'Confirm: Unfreeze coins back to the USER (valid payment verified)?';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setResolvingDisputeId(disputeId);
    try {
      const res = await sellerRateAdminAPI.resolveSellerDispute(disputeId, {
        action,
        admin_notes: note
      });
      if (res.data?.success) {
        alert(res.data.message || "Dispute ticket resolved successfully!");
        fetchAdminDisputes();
        fetchSellers();
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Failed to resolve dispute");
    } finally {
      setResolvingDisputeId(null);
    }
  };

  const handleDeductBalance = async (e) => {
    e.preventDefault();
    if (!deductingSeller) return;
    const currentBal = Number(deductingSeller.coin_balance ?? deductingSeller.current_balance ?? 0);
    const amount = parseInt(deductAmount, 10);
    if (!amount || amount <= 0) {
      setDeductError("Please enter a valid positive coin amount");
      return;
    }
    if (currentBal <= 0) {
      setDeductError("Seller currently has 0 coins. Deduction is not possible.");
      return;
    }
    if (amount > currentBal) {
      setDeductError(`Cannot deduct more than current balance (${currentBal.toLocaleString()} coins)`);
      return;
    }
    if (!deductReason.trim()) {
      setDeductError("Reason for coin deduction is required");
      return;
    }

    setDeductLoading(true);
    setDeductError('');
    setDeductSuccess('');
    try {
      const res = await sellerRateAdminAPI.deductSellerBalance(deductingSeller.id, {
        amount,
        reason: deductReason.trim()
      });
      if (res.data?.success) {
        const newBal = res.data.new_balance !== undefined ? res.data.new_balance : (currentBal - amount);
        setDeductSuccess(res.data.message || `Successfully deducted 🪙 ${amount.toLocaleString()} coins!`);
        setDeductingSeller(prev => prev ? { ...prev, coin_balance: newBal, current_balance: newBal } : null);
        setSellers(prev => prev.map(s => s.id === deductingSeller.id ? { ...s, coin_balance: newBal, current_balance: newBal } : s));
        fetchSellers();
        setTimeout(() => {
          setDeductingSeller(null);
          setDeductAmount('');
          setDeductReason('');
          setDeductSuccess('');
        }, 1500);
      }
    } catch (err) {
      setDeductError(err.response?.data?.message || err.message || "Failed to deduct seller balance");
    } finally {
      setDeductLoading(false);
    }
  };

  const handleAddCoinsSubmit = async (e) => {
    e.preventDefault();
    if (!addingCoinsSeller) return;
    const amount = parseInt(addCoinAmount, 10);
    if (!amount || amount <= 0) {
      setAddCoinError("Please enter a valid positive coin amount");
      return;
    }
    if (!addCoinReason.trim()) {
      setAddCoinError("Reason for adding coins is required");
      return;
    }

    setAddCoinLoading(true);
    setAddCoinError('');
    setAddCoinSuccess('');
    try {
      const res = await axios.post(`${constant.API_BASE_URL}/admin/business/sellers/${addingCoinsSeller.id}/add-balance`, {
        amount,
        reason: addCoinReason.trim()
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data?.success) {
        const newBal = res.data.balance !== undefined ? res.data.balance : (Number(addingCoinsSeller.coin_balance || 0) + amount);
        setAddCoinSuccess(res.data.message || `Successfully added 🪙 ${amount.toLocaleString()} coins!`);
        setAddingCoinsSeller(prev => prev ? { ...prev, coin_balance: newBal, current_balance: newBal } : null);
        setSellers(prev => prev.map(s => s.id === addingCoinsSeller.id ? { ...s, coin_balance: newBal, current_balance: newBal } : s));
        fetchSellers();
        setTimeout(() => {
          setAddingCoinsSeller(null);
          setAddCoinAmount('');
          setAddCoinReason('');
          setAddCoinSuccess('');
        }, 1500);
      }
    } catch (err) {
      setAddCoinError(err.response?.data?.message || err.message || "Failed to add coins to seller balance");
    } finally {
      setAddCoinLoading(false);
    }
  };

  const fetchRateConfig = async () => {
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/admin/business/sellers/rate-config`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success && res.data.data) {
        setRateConfig(res.data.data);
        setRateCapInput(String(res.data.data.max_coins_per_rupee || 20));
      }
    } catch (err) {
      console.error("Error fetching rate config:", err);
    }
  };

  const handleSaveRateCap = async () => {
    const val = parseFloat(rateCapInput);
    if (isNaN(val) || val <= 0) {
      alert("Please enter a valid rate greater than 0");
      return;
    }
    setSavingRateCap(true);
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/rate-config`, {
        max_coins_per_rupee: val
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setIsEditingRateCap(false);
        fetchRateConfig();
        fetchSellers();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update selling rate cap");
    } finally {
      setSavingRateCap(false);
    }
  };

  const handleSaveSellerRate = async () => {
    if (!editingSellerRate) return;
    const val = parseFloat(sellerRateInput);
    if (isNaN(val) || val <= 0) {
      alert("Please enter a valid coin rate");
      return;
    }
    if (val > rateConfig.max_coins_per_rupee) {
      alert(`Rate cannot exceed Admin maximum cap of ${rateConfig.max_coins_per_rupee} coins per ₹1.`);
      return;
    }
    setSavingSellerRate(true);
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/${editingSellerRate.id}/rate`, {
        coins_per_rupee: val
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setEditingSellerRate(null);
        fetchSellers();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update seller rate");
    } finally {
      setSavingSellerRate(false);
    }
  };

  const fetchSellers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/sellers`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setSellers(response.data.data);
      }

      // Fetch seller coin slabs
      const slabsRes = await axios.get(`${constant.API_BASE_URL}/admin/business/seller-coin-slabs`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (slabsRes.data.success) {
        const fetchedSlabs = slabsRes.data.data;
        if (slabsRes.data.usd_rate) {
          setSellerUsdRate(String(slabsRes.data.usd_rate));
        }
        setSellerCoinSlabs(fetchedSlabs.length > 0 ? fetchedSlabs : [
          { id: 1, coin_amount: 1000, price_inr: 50, discount_pct: 0, is_active: true, sort_order: 1 },
          { id: 2, coin_amount: 5000, price_inr: 250, discount_pct: 0, is_active: true, sort_order: 2 },
          { id: 3, coin_amount: 10000, price_inr: 500, discount_pct: 0, is_active: true, sort_order: 3 },
        ]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSellerUsdRateChange = (newRate) => {
    setSellerUsdRate(newRate);
    const rate = parseFloat(newRate);
    if (!isNaN(rate) && rate > 0) {
      const updated = sellerCoinSlabs.map(slab => {
        const inr = Number(slab.price_inr) || 0;
        return {
          ...slab,
          price_usd: inr > 0 ? (inr / rate).toFixed(2) : slab.price_usd
        };
      });
      setSellerCoinSlabs(updated);
    }
  };

  const handleSellerCoinSlabChange = (index, field, value) => {
    const updated = [...sellerCoinSlabs];
    if (field === 'is_active') {
      updated[index][field] = value;
      setSellerCoinSlabs(updated);
      return;
    }
    const cleanVal = String(value).replace(/[^0-9.]/g, '');
    updated[index][field] = cleanVal;

    // Auto-sync INR and USD prices using dynamic sellerUsdRate
    const rate = parseFloat(sellerUsdRate) || 94.0;
    if (field === 'price_inr') {
      const inr = parseFloat(cleanVal);
      updated[index].price_usd = !isNaN(inr) && inr > 0 ? (inr / rate).toFixed(2) : '';
    } else if (field === 'price_usd') {
      const usd = parseFloat(cleanVal);
      updated[index].price_inr = !isNaN(usd) && usd > 0 ? Math.round(usd * rate) : '';
    }

    setSellerCoinSlabs(updated);
  };

  const addNewSellerCoinSlab = () => {
    const rate = parseFloat(sellerUsdRate) || 94.0;
    setSellerCoinSlabs([...sellerCoinSlabs, {
      coin_amount: 1000, price_inr: 50, price_usd: (50 / rate).toFixed(2), discount_pct: 0, is_active: true, sort_order: sellerCoinSlabs.length + 1
    }]);
  };

  const removeSellerCoinSlab = (index) => {
    if (sellerCoinSlabs.length <= 1) return;
    const updated = [...sellerCoinSlabs];
    updated.splice(index, 1);
    updated.forEach((s, i) => s.sort_order = i + 1);
    setSellerCoinSlabs(updated);
  };

  const handleSaveSellerCoinSlabs = async () => {
    setSavingSellerCoinSlabs(true);
    try {
      const rate = parseFloat(sellerUsdRate) || 94.0;
      const formattedSlabs = sellerCoinSlabs.map((s, i) => {
        const inr = Number(s.price_inr) || 0;
        const usd = Number(s.price_usd !== undefined && s.price_usd !== '' ? s.price_usd : (inr / rate).toFixed(2)) || 0;
        return {
          ...s,
          coin_amount: Number(s.coin_amount) || 0,
          price_inr: inr,
          price_usd: usd,
          discount_pct: Number(s.discount_pct) || 0,
          sort_order: i + 1
        };
      });

      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/admin/seller-coin-slabs`, { 
        slabs: formattedSlabs,
        usd_rate: sellerUsdRate
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setSellerCoinSlabSaveSuccess(true);
        if (res.data.data) {
          setSellerCoinSlabs(res.data.data);
        }
        if (res.data.usd_rate) {
          setSellerUsdRate(String(res.data.usd_rate));
        }
        setTimeout(() => setSellerCoinSlabSaveSuccess(false), 3000);
      }
    } catch (err) {
      alert("Failed to save seller coin slabs: " + (err.response?.data?.message || err.message));
    } finally {
      setSavingSellerCoinSlabs(false);
    }
  };

  const handleToggleAppListing = async (id, currentListed) => {
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/${id}/app-listing`, {
        is_app_listed: !currentListed
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        fetchSellers();
      }
    } catch (err) {
      alert("Failed to update mobile app listing status");
    }
  };

  const handleToggleCompanySeller = async (id, currentIsCompany) => {
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/${id}/company-seller`, {
        is_company: !currentIsCompany
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        fetchSellers();
      }
    } catch (err) {
      alert("Failed to update company seller status");
    }
  };

  // Top 10 approved sellers by coin balance who are admin-listed
  const appTop10Sellers = sellers
    .filter(s => s.status === 'approved' && !s.is_frozen && s.is_app_listed !== false)
    .sort((a, b) => (Number(b.coin_balance) || 0) - (Number(a.coin_balance) || 0))
    .slice(0, 10);

  // Sellers excluded by Admin from Mobile App Top List (even if high coins)
  const excludedSellers = sellers.filter(s => s.is_app_listed === false);

  const displayedSellers = (
    viewFilter === 'TOP_APP' 
      ? appTop10Sellers 
      : viewFilter === 'EXCLUDED'
        ? excludedSellers
        : sellers
  ).filter(seller => 
    (countryFilter === 'ALL' || (seller.country && seller.country.toLowerCase() === countryFilter.toLowerCase()))
  ).filter(seller => 
    (seller.first_name && seller.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.last_name && seller.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.email && seller.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.phone && seller.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.xoxo_id && seller.xoxo_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleAddSeller = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    setModalSuccess('');

    // Validations
    if (sellerForm.whatsapp_number && sellerForm.whatsapp_number.length !== 10) {
      setModalError("WhatsApp Number must be exactly 10 digits.");
      setModalLoading(false);
      return;
    }
    if (sellerForm.aadhar_card && sellerForm.aadhar_card.length !== 12) {
      setModalError("Aadhaar Number must be exactly 12 digits.");
      setModalLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${constant.API_BASE_URL}/admin/business/sellers/create-seller`, {
        ...sellerForm,
        phone: sellerForm.whatsapp_number
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });

      if (response.data.success) {
        setModalSuccess("Seller registered and approved successfully! They can now log in at /seller/login with their credentials.");
        setSellerForm({ 
          first_name: '', 
          last_name: '', 
          email: '', 
          password: '', 
          whatsapp_number: '', 
          aadhar_card: '', 
          country: 'India',
          commission_percentage: '10.00',
          is_company: false
        });
        fetchSellers();
      }
    } catch (err) {
      setModalError(err.response?.data?.message || err.response?.data?.error || "Failed to register seller");
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/${id}/status`, { status }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        fetchSellers();
      }
    } catch (err) {
      alert("Failed to update status");
    } finally {
      setIsConfirmOpen(false);
    }
  };

  const openConfirmModal = (id, status, title) => {
    setConfirmAction({ id, status, title });
    setIsConfirmOpen(true);
  };

  const handleUpdateLevel = async (id, level) => {
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/${id}/level`, { level }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        fetchSellers();
      }
    } catch (err) {
      alert("Failed to update level");
    }
  };

  const handleToggleFreeze = async (id, currentFreeze) => {
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/${id}/freeze`, { freeze: !currentFreeze }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        fetchSellers();
      }
    } catch (err) {
      alert("Failed to toggle freeze state");
    }
  };

  const filteredSellers = sellers.filter(seller => 
    (seller.first_name && seller.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.last_name && seller.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.email && seller.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.phone && seller.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.xoxo_id && seller.xoxo_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Sellers Management</h2>
          <p className="text-slate-500 mt-1">Approve pending applications, manage seller tiers (Bronze, Silver, Gold, VIP), and track performance.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setIsAddModalOpen(true); setModalError(''); setModalSuccess(''); }}
            className="px-4 py-2.5 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-bold rounded-xl shadow-md shadow-pink-500/20 hover:scale-105 active:scale-95 transition text-sm flex items-center gap-2 cursor-pointer"
          >
            <PlusCircle size={18} />
            <span>Add Coin Seller</span>
          </button>
          <a
            href="/seller/login"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl shadow-md shadow-amber-500/20 hover:scale-105 active:scale-95 transition text-sm flex items-center gap-2"
          >
            <Coins size={16} />
            <span>Open Seller Portal</span>
          </a>
          <button 
            onClick={fetchSellers}
            className="p-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 active:scale-95 transition shadow-sm flex items-center justify-center cursor-pointer"
            title="Refresh Data"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Seller Coin Purchase Slabs (INR -> Coins) */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/40 p-8 border border-slate-100 animate-in fade-in">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-500/20 text-purple-600 rounded-2xl border border-purple-400/30">
              <Coins size={24} />
            </div>
            <div>
              <h3 className="text-xl font-black text-slate-800">Seller Recharge Slabs (₹ INR ➔ Coins 🪙)</h3>
              <p className="text-slate-500 text-xs mt-0.5">
                Configure the wholesale coin packages available for sellers to recharge their accounts.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {sellerCoinSlabSaveSuccess && (
              <div className="flex items-center gap-1.5 bg-emerald-50 text-emerald-600 px-3.5 py-2 rounded-xl text-xs font-black animate-in fade-in border border-emerald-200">
                <Check size={14} /> Saved!
              </div>
            )}
            <button
              onClick={handleSaveSellerCoinSlabs}
              disabled={savingSellerCoinSlabs}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-black rounded-2xl transition active:scale-95 shadow-lg shadow-purple-500/30 text-xs disabled:opacity-50"
            >
              <RefreshCw size={14} className={savingSellerCoinSlabs ? 'animate-spin' : ''} />
              {savingSellerCoinSlabs ? 'Saving...' : 'Save Wholesale Slabs'}
            </button>
          </div>
        </div>

        {/* Wholesale Dollar ($ USD) to INR (₹) Conversion Rate Setting Bar */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 border border-purple-200/70 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-600/10 border border-purple-300 text-purple-700 flex items-center justify-center font-bold">
              <DollarSign size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-black uppercase text-purple-900 tracking-wider">USD ($) to INR (₹) Conversion Rate</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-purple-600 text-white uppercase tracking-wider">Wholesale USD Price</span>
              </div>
              <p className="text-[11px] text-purple-700/80 mt-0.5">
                Set dollar rate for wholesale coin sellers. Changing this automatically calculates and syncs all slab dollar prices ($ USD).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 bg-white px-4 py-2 rounded-xl border border-purple-200 shadow-xs">
            <span className="text-xs font-black text-slate-700 font-mono">1 USD ($) =</span>
            <span className="text-sm font-black text-purple-700">₹</span>
            <input
              type="number"
              step="0.1"
              min="1"
              value={sellerUsdRate}
              onChange={(e) => handleSellerUsdRateChange(e.target.value)}
              placeholder="e.g. 94.00"
              className="w-24 px-2.5 py-1 bg-slate-50 border border-purple-300 rounded-lg font-black text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-center"
            />
            <span className="text-xs font-bold text-slate-400">INR</span>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-black text-slate-900 flex items-center gap-2">
            <span>Wholesale Coin Packages ({sellerCoinSlabs.length} Slabs)</span>
          </h4>
          <button
            onClick={addNewSellerCoinSlab}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-lg text-xs font-bold transition border border-purple-200"
          >
            <PlusCircle size={14} /> Add Package
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sellerCoinSlabs.map((slab, index) => {
            const c = Number(slab.coin_amount || 0);
            const p = Number(slab.price_inr || 0);
            const rate = p > 0 ? (c / p).toFixed(1) : '0.0';

            return (
              <div 
                key={slab.id || index}
                className={`p-6 rounded-3xl border transition relative overflow-hidden bg-slate-50 shadow-sm hover:shadow-md ${
                  slab.is_active ? 'border-purple-200' : 'border-slate-200 opacity-60'
                }`}
              >
                <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-purple-900 text-white flex items-center justify-center text-xs font-black">
                      {index + 1}
                    </span>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Wholesale Pkg
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={slab.is_active !== false}
                        onChange={(e) => handleSellerCoinSlabChange(index, 'is_active', e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-xs font-bold text-slate-600">
                        {slab.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </label>
                    <button
                      onClick={() => removeSellerCoinSlab(index)}
                      className="text-rose-400 hover:text-rose-600 transition"
                      title="Remove Package"
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                      <span>Coins To Receive (🪙)</span>
                      {c > 0 && <span className="text-[10px] font-black text-amber-600">🪙 {c.toLocaleString('en-IN')}</span>}
                    </label>
                    <input
                      type="text"
                      value={c > 0 ? c.toLocaleString('en-IN') : ''}
                      onChange={(e) => handleSellerCoinSlabChange(index, 'coin_amount', e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g. 10,000"
                    />
                  </div>
                  
                  {/* Dual Currency Prices Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                        <span>Price (₹ INR)</span>
                      </label>
                      <input
                        type="text"
                        value={p > 0 ? p : ''}
                        onChange={(e) => handleSellerCoinSlabChange(index, 'price_inr', e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-black text-emerald-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="e.g. 500"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                        <span>Price ($ USD)</span>
                      </label>
                      <input
                        type="text"
                        value={slab.price_usd !== undefined && slab.price_usd !== '' ? slab.price_usd : (p > 0 ? (p / 94.0).toFixed(2) : '')}
                        onChange={(e) => handleSellerCoinSlabChange(index, 'price_usd', e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-black text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g. 5.32"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-semibold">Value Rate:</span>
                  <div className="flex gap-1.5">
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-800 font-black rounded-md border border-purple-200 text-[11px]">
                      {rate} 🪙/₹
                    </span>
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-800 font-black rounded-md border border-blue-200 text-[11px]">
                      {slab.price_usd > 0 ? (c / Number(slab.price_usd)).toFixed(0) : (p > 0 ? (c / (p / 94.0)).toFixed(0) : '0')} 🪙/$
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Admin Coin Selling Cap Policy Banner */}
      <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-yellow-500/10 border-2 border-amber-400/30 rounded-[24px] p-6 shadow-sm relative overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center text-amber-600 shrink-0">
              <Coins size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-amber-500 text-white tracking-wider">
                  Admin Platform Policy
                </span>
                <h3 className="text-lg font-black text-slate-800">
                  Seller Coin Selling Rate Ceiling (Max Limit)
                </h3>
              </div>
              <p className="text-slate-600 text-xs mt-1 max-w-2xl leading-relaxed">
                Admin controls the maximum coin rate sellers are allowed to offer (e.g. max <strong>{rateConfig.max_coins_per_rupee} coins per ₹1</strong>). 
                Sellers cannot exceed this cap, but they are free to set their own lower competitive rates (e.g. 15 or 18 coins/₹1) to attract users in the mobile app.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-slate-500 font-bold">
                <span>Example: ₹20 = max {rateConfig.max_coins_per_rupee * 20} coins</span>
                <span>•</span>
                <span>₹50 = max {rateConfig.max_coins_per_rupee * 50} coins</span>
                <span>•</span>
                <span>₹100 = max {rateConfig.max_coins_per_rupee * 100} coins</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-amber-200/80 shadow-sm shrink-0 flex flex-col sm:flex-row sm:items-center gap-3">
            {isEditingRateCap ? (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    value={rateCapInput}
                    onChange={(e) => setRateCapInput(e.target.value)}
                    className="w-28 pl-3 pr-8 py-2 bg-slate-50 border border-amber-400 rounded-xl font-black text-sm text-slate-800 focus:outline-none"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">/₹1</span>
                </div>
                <button
                  onClick={handleSaveRateCap}
                  disabled={savingRateCap}
                  className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl transition flex items-center gap-1 shadow-sm"
                >
                  <Check size={14} />
                  <span>{savingRateCap ? 'Saving...' : 'Save'}</span>
                </button>
                <button
                  onClick={() => { setIsEditingRateCap(false); setRateCapInput(String(rateConfig.max_coins_per_rupee)); }}
                  className="px-2.5 py-2 text-slate-400 hover:text-slate-600 text-xs font-bold"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4">
                <div>
                  <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Current Allowed Ceiling</span>
                  <p className="text-xl font-black text-amber-600 flex items-center gap-1">
                    🪙 {rateConfig.max_coins_per_rupee} <span className="text-xs text-slate-500 font-bold">Coins / ₹1</span>
                  </p>
                </div>
                <button
                  onClick={() => setIsEditingRateCap(true)}
                  className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-sm"
                >
                  <Edit3 size={13} />
                  <span>Edit Cap</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Toolbar & Filters */}
        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setViewFilter('ALL')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                viewFilter === 'ALL'
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All Sellers ({sellers.length})
            </button>
            <button
              onClick={() => setViewFilter('TOP_APP')}
              className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ${
                viewFilter === 'TOP_APP'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/60'
              }`}
            >
              <Star size={14} className={viewFilter === 'TOP_APP' ? 'fill-white' : 'fill-amber-500'} />
              Mobile App Top 10 ({appTop10Sellers.length})
            </button>
            <button
              onClick={() => setViewFilter('EXCLUDED')}
              className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ${
                viewFilter === 'EXCLUDED'
                  ? 'bg-rose-600 text-white shadow-md'
                  : 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200/60'
              }`}
            >
              <Ban size={14} className={viewFilter === 'EXCLUDED' ? 'text-white' : 'text-rose-600'} />
              Excluded by Admin ({excludedSellers.length})
            </button>

            <button
              onClick={() => {
                setShowDisputesModal(true);
                fetchAdminDisputes();
              }}
              className="px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200/60 cursor-pointer shadow-xs"
              title="Review seller disputes and frozen user coins"
            >
              <ShieldAlert size={14} className="text-indigo-600" />
              <span>Fake Payment Disputes</span>
              {adminDisputes.filter(d => d.status === 'frozen').length > 0 && (
                <span className="px-1.5 py-0.2 bg-rose-500 text-white rounded-full text-[10px] font-black animate-pulse">
                  {adminDisputes.filter(d => d.status === 'frozen').length}
                </span>
              )}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mt-4 md:mt-0">
            <div className="flex items-center gap-2 bg-white px-3.5 py-1 rounded-2xl border border-slate-200 shadow-sm text-sm w-full sm:w-auto z-50 relative">
              <Globe size={16} className="text-pink-600 shrink-0" />
              <FilterCountrySelect
                value={countryFilter}
                onChange={(val) => setCountryFilter(val)}
                className="w-full sm:w-40"
              />
            </div>
            
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search sellers by name, email, phone, XOXO ID..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Admin Top 10 Override Authority Banner when in TOP_APP or EXCLUDED view */}
        {(viewFilter === 'TOP_APP' || viewFilter === 'EXCLUDED') && (
          <div className="mx-6 mt-4 p-4 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-md border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-pink-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                ⚡
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-extrabold text-sm text-white">Super Admin Top Sellers Power</h4>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/30 text-pink-300 border border-pink-500/40 uppercase font-black">Full Override</span>
                </div>
                <p className="text-xs text-slate-300 mt-0.5">
                  Bhale hi kisi seller ke wallet me sabse jyada coins ho, agar aap use <strong className="text-rose-400">"Remove"</strong> karenge toh wo turant Mobile App Top 10 se bahar ho jayega aur next eligible seller top me aa jayega.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Company Seller Banner when in TOP_APP view */}
        {viewFilter === 'TOP_APP' && (
          <div className="mx-6 my-4 p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500 text-white font-black flex items-center justify-center text-lg shadow-sm">
                🏢
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-extrabold text-slate-900 text-sm">Official Platform Store (Company Seller)</h4>
                  <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-black rounded-full uppercase">Rank #1 Default</span>
                </div>
                <p className="text-xs text-slate-500">Auto-pinned at Rank 1 in mobile app coin purchase dropdown with unlimited platform stock.</p>
              </div>
            </div>
            <span className="text-xs font-extrabold text-amber-800 bg-white px-3 py-1.5 rounded-xl border border-amber-200 shadow-xs">
              🪙 999,999,999 Coins
            </span>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Loading sellers directory...</p>
          </div>
        ) : displayedSellers.length === 0 ? (
          <div className="p-20 text-center text-slate-400">
            <Award size={48} className="mx-auto mb-4 opacity-30" />
            <p>
              {viewFilter === 'TOP_APP' 
                ? 'No approved sellers currently selected for Mobile App Top 10.' 
                : viewFilter === 'EXCLUDED'
                  ? 'No sellers are currently excluded. All approved sellers are eligible.'
                  : 'No coin sellers found in system.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">Seller Details</th>
                  <th className="p-6">Contact Info</th>
                  <th className="p-6">Location</th>
                  <th className="p-6">Wallet & Sales</th>
                  <th className="p-6">Selling Rate</th>
                  <th className="p-6">Mobile App List</th>
                  <th className="p-6">Seller Tier</th>
                  <th className="p-6">Commission</th>
                  <th className="p-6">Freeze State</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {displayedSellers.map((seller, index) => (
                  <tr key={seller.id} className="hover:bg-slate-50/50 transition">
                    <td className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-10 h-10 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center font-bold">
                            {seller.first_name?.[0]}{seller.last_name?.[0]}
                          </div>
                          {viewFilter === 'TOP_APP' && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-black flex items-center justify-center border border-white">
                              {index + 2}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-slate-800">{seller.first_name} {seller.last_name}</p>
                            {seller.is_company && (
                              <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-black rounded-full flex items-center gap-1 shadow-xs">
                                <Crown size={10} className="fill-white" /> COMPANY
                              </span>
                            )}
                          </div>
                          <p className="text-xs font-extrabold text-pink-600">XOXO ID: {seller.xoxo_id || seller.user_id || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6 text-slate-600">
                      <p>{seller.email}</p>
                      <p className="text-xs text-slate-400 mt-0.5">WhatsApp: {seller.whatsapp_number || seller.phone || 'N/A'}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {seller.country === 'India' ? 'Aadhar: ' : 'ID/Passport: '} 
                        {seller.aadhar_card || 'N/A'}
                      </p>
                    </td>
                    <td className="p-6">
                      <CountryBadge country={seller.country} />
                    </td>
                    <td className="p-6 font-extrabold text-slate-900 text-sm whitespace-nowrap">
                      <div>
                        <span className="text-xl">🪙 {Number(seller.coin_balance || 0).toLocaleString()}</span>
                        <p className="text-[10px] text-amber-600 font-black mt-1 uppercase tracking-wider">Seller Portal Balance</p>
                        {seller.user_wallet_balance !== undefined && (
                          <div className="mt-1.5 flex items-center gap-1 text-[11px] font-bold text-slate-500">
                            <span>App Personal:</span>
                            <span className="text-indigo-600 font-black">🪙 {Number(seller.user_wallet_balance || 0).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-100/80">
                        <p className="text-xs text-emerald-600 font-black">
                          {Number(seller.total_coins_sold || 0).toLocaleString()} Coins Sold
                        </p>
                        <p className="text-[10px] text-slate-400 font-bold">
                          Across {Number(seller.total_recharge_count || 0)} user recharges
                        </p>
                      </div>
                    </td>
                    <td className="p-6 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-xl border border-amber-200/60 text-xs">
                          🪙 {parseFloat(seller.coins_per_rupee || rateConfig.max_coins_per_rupee)} / ₹1
                        </span>
                        <button
                          onClick={() => {
                            setEditingSellerRate({
                              id: seller.id,
                              name: `${seller.first_name} ${seller.last_name}`,
                              currentRate: seller.coins_per_rupee || rateConfig.max_coins_per_rupee
                            });
                            setSellerRateInput(String(seller.coins_per_rupee || rateConfig.max_coins_per_rupee));
                          }}
                          className="p-1 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100 transition cursor-pointer"
                          title="Edit Seller Selling Rate"
                        >
                          <Edit3 size={13} />
                        </button>
                      </div>
                    </td>
                    <td className="p-6">
                      <div className="flex flex-col gap-2">
                        {seller.is_app_listed !== false ? (
                          <div className="flex flex-col gap-1.5">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 w-fit">
                              <CheckCircle2 size={12} className="text-emerald-600" />
                              Visible in App
                            </span>
                            <button
                              onClick={() => handleToggleAppListing(seller.id, true)}
                              className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 hover:border-rose-300 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer active:scale-95 shadow-xs w-fit"
                              title="Click to remove from Top Sellers (Excludes seller from app even if they have high coin balance)"
                            >
                              <XCircle size={13} className="text-rose-600" />
                              <span>Remove from App</span>
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200 w-fit">
                              <Ban size={12} className="text-rose-600" />
                              Excluded by Admin
                            </span>
                            <button
                              onClick={() => handleToggleAppListing(seller.id, false)}
                              className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 hover:border-emerald-300 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer active:scale-95 shadow-xs w-fit"
                              title="Click to add back to Top Sellers"
                            >
                              <PlusCircle size={13} className="text-emerald-600" />
                              <span>Add Back to App</span>
                            </button>
                          </div>
                        )}

                        {/* Company Seller toggle */}
                        <button
                          onClick={() => handleToggleCompanySeller(seller.id, seller.is_company)}
                          className={`mt-0.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border transition cursor-pointer w-fit ${
                            seller.is_company
                              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white border-amber-500 shadow-sm hover:from-amber-600 hover:to-orange-600'
                              : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-amber-50 hover:text-amber-800 hover:border-amber-300'
                          }`}
                          title={seller.is_company ? "Click to revoke Company Seller status" : "Click to mark as Official Company Seller (is_company: true)"}
                        >
                          <Crown size={12} className={seller.is_company ? "fill-white" : "text-amber-600"} />
                          <span>{seller.is_company ? "Company: TRUE" : "Set as Company"}</span>
                        </button>
                      </div>
                    </td>
                    <td className="p-6">
                      <select 
                        value={seller.level} 
                        onChange={(e) => handleUpdateLevel(seller.id, e.target.value)}
                        className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-pink-500/20 cursor-pointer"
                      >
                        <option value="BRONZE">BRONZE</option>
                        <option value="SILVER">SILVER</option>
                        <option value="GOLD">GOLD</option>
                        <option value="VIP">VIP</option>
                      </select>
                    </td>
                    <td className="p-6 font-bold text-slate-700">{seller.commission_percentage}%</td>
                    <td className="p-6">
                      <button
                        onClick={() => handleToggleFreeze(seller.id, seller.is_frozen)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase transition cursor-pointer ${
                          seller.is_frozen ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-600 border border-green-100'
                        }`}
                      >
                        {seller.is_frozen ? <Ban size={12} /> : <Unlock size={12} />}
                        {seller.is_frozen ? 'Frozen' : 'Active'}
                      </button>
                    </td>
                    <td className="p-6">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        seller.status === 'approved' ? 'bg-green-100 text-green-700' :
                        seller.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {seller.status}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex justify-end items-center gap-2">
                        <button 
                          onClick={() => {
                            setAddingCoinsSeller(seller);
                            setAddCoinAmount('');
                            setAddCoinReason('');
                            setAddCoinError('');
                            setAddCoinSuccess('');
                          }}
                          className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer"
                          title="Add coins directly to seller's merchant wallet"
                        >
                          <PlusCircle size={12} className="text-emerald-600" />
                          <span>Add Coins</span>
                        </button>
                        <button 
                          onClick={() => {
                            setDeductingSeller(seller);
                            setDeductAmount('');
                            setDeductReason('');
                            setDeductError('');
                            setDeductSuccess('');
                          }}
                          className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer"
                          title="Deduct coins from seller's wallet"
                        >
                          <Coins size={12} className="text-amber-600" />
                          <span>Deduct</span>
                        </button>

                        {seller.status === 'pending' && (
                          <>
                            <button 
                              onClick={() => openConfirmModal(seller.id, 'approved', 'Approve this seller?')}
                              className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs font-bold hover:bg-green-600 active:scale-95 transition cursor-pointer"
                            >
                              Approve
                            </button>
                            <button 
                              onClick={() => openConfirmModal(seller.id, 'rejected', 'Reject this seller?')}
                              className="px-3 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-bold hover:bg-red-100 active:scale-95 transition cursor-pointer"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {seller.status === 'approved' && (
                          <button 
                            onClick={() => handleUpdateStatus(seller.id, 'blocked')}
                            className="px-3 py-1 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 active:scale-95 transition cursor-pointer"
                          >
                            Block Seller
                          </button>
                        )}
                        {seller.status === 'blocked' && (
                          <button 
                            onClick={() => handleUpdateStatus(seller.id, 'approved')}
                            className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs font-bold hover:bg-green-600 active:scale-95 transition cursor-pointer"
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {isConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm p-6 text-center animate-in fade-in zoom-in duration-200">
            <div className="w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">{confirmAction.title}</h3>
            <p className="text-slate-500 text-sm mb-6">Are you sure you want to proceed with this action? This cannot be undone immediately.</p>
            <div className="flex gap-3 justify-center">
              <button 
                onClick={() => setIsConfirmOpen(false)}
                className="px-6 py-2.5 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition cursor-pointer"
              >
                Cancel
              </button>
              <button 
                onClick={() => handleUpdateStatus(confirmAction.id, confirmAction.status)}
                className="px-6 py-2.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition active:scale-95 cursor-pointer"
              >
                Yes, Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Coin Seller Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-200">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white">
              <h3 className="text-xl font-bold text-slate-800">Add New Coin Seller</h3>
              <button 
                onClick={() => setIsAddModalOpen(false)} 
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50 transition cursor-pointer"
              >
                <X size={24} />
              </button>
            </div>

            {/* Error / Success Notifications */}
            <div className="px-6 mt-4">
              {modalError && (
                <div className="p-4 bg-red-50 text-red-600 border border-red-100 rounded-xl text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle size={18} /> {modalError}
                </div>
              )}
              {modalSuccess && (
                <div className="p-4 bg-green-50 text-green-600 border border-green-100 rounded-xl text-sm font-semibold flex items-center gap-2">
                  <CheckCircle2 size={18} /> {modalSuccess}
                </div>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleAddSeller} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  required
                  placeholder="First Name"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={sellerForm.first_name}
                  onChange={(e) => setSellerForm({ ...sellerForm, first_name: e.target.value })}
                />
                <input
                  required
                  placeholder="Last Name"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={sellerForm.last_name}
                  onChange={(e) => setSellerForm({ ...sellerForm, last_name: e.target.value })}
                />
              </div>

              <input
                required
                type="email"
                placeholder="Seller Email Address"
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                value={sellerForm.email}
                onChange={(e) => setSellerForm({ ...sellerForm, email: e.target.value })}
              />

              <div className="relative w-full">
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  placeholder="Seller Access Password"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition pr-10"
                  value={sellerForm.password}
                  onChange={(e) => setSellerForm({ ...sellerForm, password: e.target.value })}
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-pink-600 transition"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="px-1">
                <p className="text-xs font-semibold text-slate-500">
                  Strength Requirements: 
                  <span className={sellerForm.password.length >= 6 ? "text-green-500 ml-1" : "text-slate-400 ml-1"}>Min 6 chars</span>
                </p>
              </div>

              <div className="relative">
                <SearchableCountrySelect
                  value={sellerForm.country}
                  onChange={(val) => setSellerForm({...sellerForm, country: val})}
                />
              </div>

              <div className="mt-4">
                <input
                  required
                  placeholder="WhatsApp Number (Mandatory)"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={sellerForm.whatsapp_number}
                  onChange={(e) => setSellerForm({ ...sellerForm, whatsapp_number: e.target.value })}
                />
                <p className="text-[10px] text-slate-400 mt-1 pl-1">Required for 2FA and transactional alerts.</p>
              </div>

              <div className="mt-4">
                <input
                  placeholder={sellerForm.country === 'India' ? "Aadhar Card (Optional)" : "National ID / Passport (Optional)"}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={sellerForm.aadhar_card}
                  onChange={(e) => setSellerForm({ ...sellerForm, aadhar_card: e.target.value })}
                />
                <p className="text-[10px] text-slate-400 mt-1 pl-1">Helps in faster KYC and verified badge allocation.</p>
              </div>

              {/* Initial Selling Rate */}
              <div className="mt-4">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Initial Selling Rate (Coins per ₹1)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    max={rateConfig.max_coins_per_rupee}
                    placeholder="e.g. 20.00"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition font-bold"
                    value={sellerForm.coins_per_rupee}
                    onChange={(e) => setSellerForm({ ...sellerForm, coins_per_rupee: e.target.value })}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                    Max: {rateConfig.max_coins_per_rupee} / ₹1
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1 pl-1">
                  Sellers can also adjust their competitive rate later in their Seller Portal.
                </p>
              </div>

              {/* Company Seller Checkbox */}
              <div className="mt-4 p-3 bg-amber-50/80 border border-amber-200 rounded-xl flex items-center gap-3">
                <input
                  type="checkbox"
                  id="modal_is_company"
                  checked={sellerForm.is_company || false}
                  onChange={(e) => setSellerForm({ ...sellerForm, is_company: e.target.checked })}
                  className="w-4 h-4 text-amber-600 rounded cursor-pointer accent-amber-600"
                />
                <label htmlFor="modal_is_company" className="text-xs font-bold text-slate-800 cursor-pointer flex items-center gap-1.5">
                  <Crown size={14} className="text-amber-600" />
                  <span>Mark as Official Company Seller (<code className="text-amber-700 font-mono">is_company: true</code>)</span>
                </label>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setIsAddModalOpen(false)} 
                  className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={modalLoading} 
                  className="px-6 py-2.5 bg-pink-600 hover:bg-pink-700 text-white rounded-xl font-bold shadow-lg shadow-pink-200 transition cursor-pointer"
                >
                  {modalLoading ? "Creating..." : "Apply & Add Seller"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Single Seller Rate Modal */}
      {editingSellerRate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-in zoom-in-95">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-amber-500/20 text-amber-600 flex items-center justify-center">
                  <Coins size={16} />
                </div>
                <h3 className="font-bold text-slate-800 text-base">Edit Selling Rate</h3>
              </div>
              <button onClick={() => setEditingSellerRate(null)} className="text-slate-400 hover:text-slate-600 p-1">
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Set custom selling rate for <strong>{editingSellerRate.name}</strong>. Rate cannot exceed Admin cap of <strong>{rateConfig.max_coins_per_rupee} Coins / ₹1</strong>.
            </p>

            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Coins per 1 Rupee (₹1)
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.5"
                  min="1"
                  max={rateConfig.max_coins_per_rupee}
                  value={sellerRateInput}
                  onChange={(e) => setSellerRateInput(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:ring-2 focus:ring-amber-500/20"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                  Coins / ₹1
                </span>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setEditingSellerRate(null)}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSellerRate}
                disabled={savingSellerRate}
                className="px-4 py-2 text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white rounded-xl shadow-sm transition"
              >
                {savingSellerRate ? 'Saving...' : 'Update Rate'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Deduct Seller Balance Modal */}
      {deductingSeller && (() => {
        const currentBal = Number(deductingSeller.coin_balance ?? deductingSeller.current_balance ?? 0);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in zoom-in-95">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-amber-500/20 text-amber-600 flex items-center justify-center">
                    <Coins size={16} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-base">Deduct Seller Balance</h3>
                    <p className="text-xs text-slate-500">Manual coin deduction by admin</p>
                  </div>
                </div>
                <button 
                  onClick={() => setDeductingSeller(null)} 
                  className="text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl mb-4 text-xs space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Seller:</span>
                  <span className="font-bold text-slate-800">{deductingSeller.first_name} {deductingSeller.last_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Current Balance:</span>
                  <span className="font-black text-amber-600 text-sm">🪙 {currentBal.toLocaleString()} Coins</span>
                </div>
                {currentBal <= 0 && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800 font-semibold flex items-center gap-1.5">
                    <AlertCircle size={14} className="text-amber-600 shrink-0" />
                    <span>This seller currently has 0 coins. Deduction is unavailable.</span>
                  </div>
                )}
              </div>

              {deductError && (
                <div className="p-3 mb-4 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 font-semibold flex items-center gap-2">
                  <AlertCircle size={15} className="shrink-0" />
                  <span>{deductError}</span>
                </div>
              )}

              {deductSuccess && (
                <div className="p-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700 font-semibold flex items-center gap-2">
                  <CheckCircle size={15} className="shrink-0" />
                  <span>{deductSuccess}</span>
                </div>
              )}

              <form onSubmit={handleDeductBalance} className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="text-xs font-bold text-slate-700">
                      Coins to Deduct
                    </label>
                    {currentBal > 0 && (
                      <button
                        type="button"
                        onClick={() => setDeductAmount(String(currentBal))}
                        className="text-[11px] font-bold text-amber-600 hover:text-amber-700 underline cursor-pointer"
                      >
                        Deduct All ({currentBal.toLocaleString()})
                      </button>
                    )}
                  </div>
                  <input
                    type="number"
                    min="1"
                    max={currentBal > 0 ? currentBal : undefined}
                    value={deductAmount}
                    onChange={(e) => setDeductAmount(e.target.value)}
                    placeholder={currentBal > 0 ? "e.g. 5000" : "0 balance - cannot deduct"}
                    disabled={currentBal <= 0 || deductLoading}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:ring-2 focus:ring-amber-500/20 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    Reason for Deduction
                  </label>
                  <textarea
                    rows={2}
                    value={deductReason}
                    onChange={(e) => setDeductReason(e.target.value)}
                    placeholder="Admin penalty / correction / manual settlement..."
                    disabled={currentBal <= 0 || deductLoading}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 outline-none focus:ring-2 focus:ring-amber-500/20 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                    required
                  />
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <button
                    type="button"
                    onClick={() => setDeductingSeller(null)}
                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={deductLoading || currentBal <= 0}
                    className="px-4 py-2 text-xs font-bold bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl shadow-sm transition flex items-center gap-1.5 cursor-pointer"
                  >
                    {deductLoading ? (
                      <span>Deducting...</span>
                    ) : (
                      <>
                        <Coins size={14} />
                        <span>{currentBal <= 0 ? "Zero Balance" : "Confirm Deduct"}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Add Coins to Seller Wallet Modal */}
      {addingCoinsSeller && (() => {
        const currentBal = Number(addingCoinsSeller.coin_balance ?? addingCoinsSeller.current_balance ?? 0);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in zoom-in-95">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-emerald-500/20 text-emerald-600 flex items-center justify-center">
                    <PlusCircle size={16} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-base">Add Coins to Seller</h3>
                    <p className="text-xs text-slate-500">Credit merchant coins to seller wallet</p>
                  </div>
                </div>
                <button 
                  onClick={() => setAddingCoinsSeller(null)} 
                  className="text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl mb-4 text-xs space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Seller:</span>
                  <span className="font-bold text-slate-800">{addingCoinsSeller.first_name} {addingCoinsSeller.last_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Seller Portal Balance:</span>
                  <span className="font-black text-amber-600 text-sm">🪙 {currentBal.toLocaleString()} Coins</span>
                </div>
                {addingCoinsSeller.user_wallet_balance !== undefined && (
                  <div className="flex justify-between items-center pt-1 border-t border-slate-200/60">
                    <span className="text-slate-500">Personal App Balance:</span>
                    <span className="font-bold text-indigo-600 text-xs">🪙 {Number(addingCoinsSeller.user_wallet_balance).toLocaleString()} Coins</span>
                  </div>
                )}
              </div>

              {addCoinError && (
                <div className="p-3 mb-4 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 font-semibold flex items-center gap-2">
                  <AlertCircle size={15} className="shrink-0" />
                  <span>{addCoinError}</span>
                </div>
              )}

              {addCoinSuccess && (
                <div className="p-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700 font-semibold flex items-center gap-2">
                  <CheckCircle size={15} className="shrink-0" />
                  <span>{addCoinSuccess}</span>
                </div>
              )}

              <form onSubmit={handleAddCoinsSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    Coins to Add
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={addCoinAmount}
                    onChange={(e) => setAddCoinAmount(e.target.value)}
                    placeholder="e.g. 100000"
                    disabled={addCoinLoading}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                    required
                  />
                  <div className="flex gap-2 mt-2">
                    {[50000, 100000, 500000, 1000000].map(amt => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => setAddCoinAmount(String(amt))}
                        className="px-2 py-1 bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 border border-slate-200 rounded-lg text-[10px] font-bold text-slate-600 cursor-pointer transition"
                      >
                        +{amt >= 1000000 ? `${amt / 1000000}M` : `${amt / 1000}k`}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    Reason / Note
                  </label>
                  <textarea
                    rows={2}
                    value={addCoinReason}
                    onChange={(e) => setAddCoinReason(e.target.value)}
                    placeholder="Admin wholesale recharge / manual addition..."
                    disabled={addCoinLoading}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                    required
                  />
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <button
                    type="button"
                    onClick={() => setAddingCoinsSeller(null)}
                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addCoinLoading}
                    className="px-4 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl shadow-sm transition flex items-center gap-1.5 cursor-pointer"
                  >
                    {addCoinLoading ? (
                      <span>Adding...</span>
                    ) : (
                      <>
                        <PlusCircle size={14} />
                        <span>Confirm Add Coins</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Admin Payment Disputes & Frozen User Coins Modal */}
      {showDisputesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-[28px] shadow-2xl w-full max-w-4xl p-6 md:p-8 animate-in zoom-in-95 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-rose-50 text-rose-600 border border-rose-200/60 flex items-center justify-center">
                  <ShieldAlert size={22} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 text-lg">Seller Fake Payment Disputes</h3>
                  <p className="text-xs text-slate-500">Disputes raised when sellers transferred coins but received fake or no payment</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchAdminDisputes}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition"
                  title="Refresh disputes"
                >
                  <RefreshCw size={16} className={loadingAdminDisputes ? "animate-spin" : ""} />
                </button>
                <button 
                  onClick={() => setShowDisputesModal(false)} 
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {loadingAdminDisputes ? (
                <div className="p-12 text-center text-slate-400">
                  <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm">Loading payment disputes...</p>
                </div>
              ) : adminDisputes.length === 0 ? (
                <div className="p-12 text-center text-slate-400">
                  <ShieldCheck size={40} className="mx-auto mb-3 opacity-30 text-emerald-500" />
                  <p className="text-base font-bold text-slate-700">All clear! No disputes pending.</p>
                  <p className="text-xs text-slate-400 mt-1">When a seller reports fake payment, their ticket and frozen coins will appear here.</p>
                </div>
              ) : (
                adminDisputes.map((dispute) => {
                  const isPending = dispute.status === 'frozen';
                  return (
                    <div key={dispute.id} className="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-extrabold text-slate-900 text-sm">
                            User: {dispute.user_first_name ? `${dispute.user_first_name} ${dispute.user_last_name || ''}` : dispute.user_name || 'User'}
                          </span>
                          {dispute.user_xoxo_id && (
                            <span className="px-2 py-0.5 rounded-md bg-pink-50 text-pink-700 border border-pink-200 font-mono text-xs">
                              XOXO: {dispute.user_xoxo_id}
                            </span>
                          )}
                          <span className="text-xs text-slate-400">|</span>
                          <span className="text-xs text-slate-600">
                            Seller: <strong>{dispute.seller_first_name} {dispute.seller_last_name}</strong>
                          </span>
                        </div>

                        <span className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider ${
                          isPending 
                            ? 'bg-amber-100 text-amber-800 border border-amber-300' 
                            : dispute.status === 'resolved_refunded'
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                            : 'bg-slate-200 text-slate-700'
                        }`}>
                          {isPending ? '⚠️ Coins Frozen' : dispute.status === 'resolved_refunded' ? '✅ Refunded to Seller' : dispute.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs bg-white p-3 rounded-xl border border-slate-200/80">
                        <div>
                          <span className="text-slate-400 block mb-0.5">Frozen Coin Amount</span>
                          <span className="text-base font-extrabold text-rose-600">
                            🪙 {Number(dispute.coins_amount).toLocaleString()} Coins
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block mb-0.5">Date Raised</span>
                          <span className="text-slate-700 font-semibold">
                            {dispute.created_at ? new Date(dispute.created_at).toLocaleString() : '—'}
                          </span>
                        </div>
                      </div>

                      <div className="text-xs bg-rose-50/50 p-3 rounded-xl border border-rose-100">
                        <strong className="text-rose-800">Seller Reason:</strong>
                        <p className="text-slate-700 mt-0.5">{dispute.reason}</p>
                        {dispute.seller_proof_url && (
                          <a 
                            href={dispute.seller_proof_url} 
                            target="_blank" 
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-rose-600 hover:underline font-bold mt-1.5"
                          >
                            <ExternalLink size={12} />
                            View Seller Proof Link
                          </a>
                        )}
                      </div>

                      {dispute.user_proof_notes && (
                        <div className="text-xs bg-blue-50/50 p-3 rounded-xl border border-blue-100">
                          <strong className="text-blue-800">User Submission:</strong>
                          <p className="text-slate-700 mt-0.5">{dispute.user_proof_notes}</p>
                          {dispute.user_proof_url && (
                            <a 
                              href={dispute.user_proof_url} 
                              target="_blank" 
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 hover:underline font-bold mt-1.5"
                            >
                              <ExternalLink size={12} />
                              View User Proof Link
                            </a>
                          )}
                        </div>
                      )}

                      {isPending ? (
                        <div className="pt-2 border-t border-slate-200/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <input
                            type="text"
                            placeholder="Optional admin review notes..."
                            value={disputeAdminNote[dispute.id] || ''}
                            onChange={(e) => setDisputeAdminNote({ ...disputeAdminNote, [dispute.id]: e.target.value })}
                            className="flex-1 px-3 py-2 text-xs bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20"
                          />
                          <div className="flex items-center gap-2">
                            <button
                              disabled={resolvingDisputeId === dispute.id}
                              onClick={() => handleResolveDispute(dispute.id, 'UNFREEZE_TO_USER')}
                              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition cursor-pointer"
                              title="Payment is valid: Unfreeze and return coins to the user"
                            >
                              Unfreeze to User
                            </button>
                            <button
                              disabled={resolvingDisputeId === dispute.id}
                              onClick={() => handleResolveDispute(dispute.id, 'REFUND_TO_SELLER')}
                              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-sm transition cursor-pointer flex items-center gap-1"
                              title="Fake payment confirmed: Refund coins to seller"
                            >
                              <CheckCircle size={14} />
                              <span>Refund to Seller</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        dispute.admin_notes && (
                          <div className="text-xs text-slate-500 italic bg-white p-2.5 rounded-xl border border-slate-200">
                            Admin Note: {dispute.admin_notes}
                          </div>
                        )
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="pt-3 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setShowDisputesModal(false)}
                className="px-5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellersManagement;
